import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from "../../../Config/axiosInstance";
import "./EditExternalResourceInvoice.css";
import { createPortal } from "react-dom";
import { Typography } from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const EditExternalResourceInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    project_code: "",
    cost_id: "",
    supplier: "",
    invoice_number: "",
    invoice_date: "",
    bill_maturity: "",
    invoice_date_display: "",
    bill_maturity_display: "",
    invoice_without_vat: "",
    supplier_vat: "21",
    total_invoice_with_vat: "",
    currency: "",
    exchange_rate: "",
    invoice_without_vat_system: "",
    total_invoice_with_vat_system: "",
    medium_alert: "5",
    critical_alert: "1",
    remarks: "",
    month: "",
    year: "",
    payments_status: "Unpaid"
  });

  const animatedComponents = makeAnimated();

  // All suppliers
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [errors, setErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const map = new Map();

    for (const rate of exchangeRates) {
      const currencyId = Number(rate.currency);
      if (!map.has(currencyId)) map.set(currencyId, []);

      map.get(currencyId).push({
        value: rate.id,
        label: String(rate.value ?? ""),
        dateLabel: rate.date || "",
        rateValue: Number(rate.value),
        rateDate: rate.date,
      });
    }

    for (const [currencyId, options] of map.entries()) {
      options.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      map.set(currencyId, options);
    }

    return map;
  }, [exchangeRates]);

  const isSystemCurrency = useMemo(() => {
    if (!formData.currency || !systemCurrencyId) return false;
    return Number(formData.currency) === Number(systemCurrencyId);
  }, [formData.currency, systemCurrencyId]);

  const exchangeOptions = useMemo(() => {
    if (!formData.currency) return [];
    return exchangeOptionsByCurrencyId.get(Number(formData.currency)) || [];
  }, [formData.currency, exchangeOptionsByCurrencyId]);

  const calculateSystemAmounts = useCallback(
    (invoiceWithoutVat, totalWithVat, currencyId, exchangeRateOption) => {
      const withoutVat = Number(invoiceWithoutVat);
      const withVat = Number(totalWithVat);

      if (!currencyId || !systemCurrencyId) {
        return {
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
        };
      }

      if (Number(currencyId) === Number(systemCurrencyId)) {
        return {
          invoice_without_vat_system: Number.isFinite(withoutVat)
            ? withoutVat.toFixed(2)
            : "",
          total_invoice_with_vat_system: Number.isFinite(withVat)
            ? withVat.toFixed(2)
            : "",
        };
      }

      const rateValue = Number(exchangeRateOption?.rateValue);

      if (!Number.isFinite(rateValue) || rateValue <= 0) {
        return {
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
        };
      }

      return {
        invoice_without_vat_system: Number.isFinite(withoutVat)
          ? (withoutVat * rateValue).toFixed(2)
          : "",
        total_invoice_with_vat_system: Number.isFinite(withVat)
          ? (withVat * rateValue).toFixed(2)
          : "",
      };
    },
    [systemCurrencyId]
  );

  const handleSupplierChange = (selectedOption) => {
    setFormData({ ...formData, supplier: selectedOption ? selectedOption.value : "" });
    if (fieldErrors.supplier) {
      setFieldErrors(prev => ({ ...prev, supplier: "" }));
    }
  };

  const handleCurrencyChange = (selectedOption) => {
    setSelectedCurrency(selectedOption);

    if (!selectedOption) {
      setSelectedExchangeRate(null);

      setFormData((prev) => ({
        ...prev,
        currency: "",
        exchange_rate: "",
        invoice_without_vat_system: "",
        total_invoice_with_vat_system: "",
      }));

      setFieldErrors((prev) => ({
        ...prev,
        currency: "",
        exchange_rate: "",
      }));

      return;
    }

    if (
      systemCurrencyId &&
      Number(selectedOption.value) === Number(systemCurrencyId)
    ) {
      const systemAmounts = calculateSystemAmounts(
        formData.invoice_without_vat,
        formData.total_invoice_with_vat,
        selectedOption.value,
        null
      );

      setSelectedExchangeRate(null);

      setFormData((prev) => ({
        ...prev,
        currency: selectedOption.value,
        exchange_rate: "",
        ...systemAmounts,
      }));
    } else {
      const optionsForCurrency =
        exchangeOptionsByCurrencyId.get(Number(selectedOption.value)) || [];

      const latestRate = optionsForCurrency.length ? optionsForCurrency[0] : null;

      const systemAmounts = calculateSystemAmounts(
        formData.invoice_without_vat,
        formData.total_invoice_with_vat,
        selectedOption.value,
        latestRate
      );

      setSelectedExchangeRate(latestRate);

      setFormData((prev) => ({
        ...prev,
        currency: selectedOption.value,
        exchange_rate: latestRate ? latestRate.value : "",
        ...systemAmounts,
      }));
    }

    setFieldErrors((prev) => ({
      ...prev,
      currency: "",
      exchange_rate: "",
    }));
  };

  const handleExchangeRateChange = (selectedOption) => {
    setSelectedExchangeRate(selectedOption);

    const systemAmounts = calculateSystemAmounts(
      formData.invoice_without_vat,
      formData.total_invoice_with_vat,
      formData.currency,
      selectedOption
    );

    setFormData((prev) => ({
      ...prev,
      exchange_rate: selectedOption ? selectedOption.value : "",
      ...systemAmounts,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      exchange_rate: "",
    }));
  };

  // Fetch all suppliers initially
  useEffect(() => {
    axiosInstance
      .get("/suppliers/")
      .then((response) => {
        const supplierOptions = response.data.map((supplier) => ({
          value: supplier.id,
          label: supplier.name,
        }));
        setAllSuppliers(supplierOptions);
      })
      .catch((error) => console.error("Error fetching suppliers:", error));
  }, []);

  useEffect(() => {
    const fetchCurrencyData = async () => {
      try {
        const [currenciesRes, exchangeRes, systemRes] = await Promise.all([
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
        ]);

        const formattedCurrencies = (currenciesRes.data || []).map((curr) => ({
          value: curr.id,
          label: curr.abbreviation || curr.name,
          abbreviation: curr.abbreviation,
          name: curr.name,
        }));

        setCurrencies(formattedCurrencies);

        setExchangeRates(
          (exchangeRes.data || []).sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );

        if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
          const currencyId = systemRes.data[0]?.currency ?? null;
          setSystemCurrencyId(currencyId || null);

          const sys = formattedCurrencies.find(
            (c) => Number(c.value) === Number(currencyId)
          );

          setSystemCurrencyLabel(sys?.abbreviation || sys?.name || "");
        }
      } catch (error) {
        console.error("Error loading currency data:", error);
      }
    };

    fetchCurrencyData();
  }, []);

  // Project Codes
  const [projectCodes, setProjectCodes] = useState([]);
  const [selectedProjectCode, setSelectedProjectCode] = useState(null);

  useEffect(() => {
    axiosInstance
      .get('/project-codes/')
      .then(response => {
        const formattedProjectCodes = response.data.map(pc => ({
          value: pc.id,
          label: `${pc.code} - ${pc.description}`
        }));
        setProjectCodes(formattedProjectCodes);
      })
      .catch(error => console.error("Error fetching project codes:", error));
  }, []);

  const handleProjectCodeChange = (selectedOption) => {
    setSelectedProjectCode(selectedOption);
    setSelectedCost(null);
    setFormData((prevFormData) => ({
      ...prevFormData,
      project_code: selectedOption ? selectedOption.value : null,
      cost_id: "",
      supplier: ""
    }));
    setFilteredCost([]);
    setFilteredSuppliers([]);

    if (fieldErrors.project_code) {
      setFieldErrors(prev => ({ ...prev, project_code: "" }));
    }
  };

  // Costs - Fetch all with source_type = External
  const [costId, setCosts] = useState([]);
  const [selectedCost, setSelectedCost] = useState(null);

  // Fetch costs elemet
  const fetchCosts = useCallback(async () => {
    try {
      const [projectBudgetRes, budgetStructureRes] = await Promise.all([
        axiosInstance.get('/project-budget/'),
        axiosInstance.get('/budget-structure/')
      ]);

      const externalCosts = projectBudgetRes.data.filter(
        (pb) => pb.source_type === "External"
      );

      const budgetStructures = budgetStructureRes.data;

      const updatedDCosts = externalCosts.map((dl) => {
        const matchedStructure = budgetStructures.find(
          (bs) => bs.id === dl.cost_element
        );

        const CostDetails = {
          number: matchedStructure?.cost_element_id || dl.cost_element_id || "N/A",
          description: matchedStructure?.cost_element_description || dl.cost_elemet_description || "No description",
          projectCode: matchedStructure?.project_code_details?.project_code || dl.project_code,
          projectDetails: matchedStructure?.project_code_details || null,
          offerDetails: matchedStructure?.offer_code_details || null,
          externalSupplier: dl.supplier
        };

        return {
          value: dl.id,
          label: `${CostDetails.number} - ${CostDetails.description}`,
          projectCode: CostDetails.projectCode,
          projectDetails: CostDetails.projectDetails,
          offerDetails: CostDetails.offerDetails,
          externalSupplier: CostDetails.externalSupplier,
          originalData: {
            cost_element: dl,
            matchedStructure: matchedStructure
          }
        };
      });

      setCosts(updatedDCosts);
    } catch (error) {
      console.error("Error fetching costs:", error);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  const [filteredCosts, setFilteredCost] = useState([]);

  useEffect(() => {
    if (formData.project_code && costId.length > 0) {
      const selectedProject = projectCodes.find(pc => pc.value === formData.project_code);
      const selectedProjectCode = selectedProject?.label.split(' - ')[0];

      const filtered = costId.filter(cost_element =>
        cost_element.projectCode === selectedProjectCode
      );

      setFilteredCost(filtered);
    } else {
      setFilteredCost([]);
    }
  }, [formData.project_code, costId, projectCodes]);

  // Sync selectedCost with formData.cost_id
  useEffect(() => {
    if (formData.cost_id) {
      const cost_element = filteredCosts.find(
        d => d.value === formData.cost_id
      );
      setSelectedCost(cost_element || null);
    } else {
      setSelectedCost(null);
    }
  }, [formData.cost_id, filteredCosts]);

  // Filter suppliers when cost_element is selected
  const handleCostChange = (selectedOption) => {
    setSelectedCost(selectedOption);
    setFormData(prev => ({
      ...prev,
      cost_id: selectedOption ? selectedOption.value : "",
      supplier: ""
    }));

    if (selectedOption && selectedOption.externalSupplier) {
      const matchingSupplier = allSuppliers.find(
        s => s.value === selectedOption.externalSupplier
      );
      if (matchingSupplier) {
        setFilteredSuppliers([matchingSupplier]);
        // If editing existing invoice with matching supplier, auto-select it
        if (id && formData.supplier === matchingSupplier.value) {
          setFormData(prev => ({
            ...prev,
            supplier: matchingSupplier.value
          }));
        }
      } else {
        setFilteredSuppliers([]);
      }
    } else {
      setFilteredSuppliers([]);
    }

    if (fieldErrors.cost_id) {
      setFieldErrors(prev => ({ ...prev, cost_id: "" }));
    }
  };

  // Month choices
  const [monthChoices, setMonthChoices] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/month-choices/")
      .then((response) => {
        if (response.data.month_choices) {
          const options = response.data.month_choices.map(([value, label]) => ({
            value,
            label
          }));
          setMonthChoices(options);
        }
      })
      .catch((error) => console.error("Error fetching month choices:", error));
  }, []);

  
  useEffect(() => {
    if (formData.invoice_date) {
      const date = new Date(formData.invoice_date);
      const year = date.getFullYear().toString();
      const monthValue = String(date.getMonth() + 1).padStart(2, '0');

      setFormData(prevData => ({
        ...prevData,
        year: year,
        month: monthValue
      }));

      // Setează și datele pentru afișare
      if (formData.invoice_date && !formData.invoice_date_display) {
        setFormData(prev => ({
          ...prev,
          invoice_date_display: format(new Date(formData.invoice_date), "dd.MM.yyyy")
        }));
      }

      // Șterge erorile dacă există
      setFieldErrors(prev => ({
        ...prev,
        month: "",
        year: ""
      }));
    }
  }, [formData.invoice_date, formData.invoice_date_display]);

  // Setează și data de scadență pentru afișare
  useEffect(() => {
    if (formData.bill_maturity && !formData.bill_maturity_display) {
      setFormData(prev => ({
        ...prev,
        bill_maturity_display: format(new Date(formData.bill_maturity), "dd.MM.yyyy")
      }));
    }
  }, [formData.bill_maturity, formData.bill_maturity_display]);;

  // Load existing invoice for editing
  useEffect(() => {
    if (id) {
      axiosInstance.get(`/external-resource-invoice/${id}/`)
        .then(response => {
          const data = response.data;

          // Formatează datele pentru afișare
          const invoiceDateDisplay = data.invoice_date
            ? format(new Date(data.invoice_date), "dd.MM.yyyy")
            : "";

          const billMaturityDisplay = data.bill_maturity
            ? format(new Date(data.bill_maturity), "dd.MM.yyyy")
            : "";

          setFormData({
            project_code: data.project_code,
            cost_id: data.cost_id,
            supplier: data.supplier,
            invoice_number: data.invoice_number,
            invoice_date: data.invoice_date,
            invoice_date_display: invoiceDateDisplay,
            bill_maturity: data.bill_maturity,
            bill_maturity_display: billMaturityDisplay,
            invoice_without_vat: data.invoice_without_vat,
            supplier_vat: data.supplier_vat,
            total_invoice_with_vat: data.total_invoice_with_vat,
            currency: data.currency || "",
            exchange_rate: data.exchange_rate || "",
            invoice_without_vat_system: data.invoice_without_vat_system || "",
            total_invoice_with_vat_system: data.total_invoice_with_vat_system || "",
            medium_alert: data.medium_alert,
            critical_alert: data.critical_alert,
            remarks: data.remarks,
            month: data.month,
            year: data.year,
            payments_status: data.payments_status,
          });

          // Preselect project code
          const selectedPC = projectCodes.find(pc => pc.value === data.project_code);
          setSelectedProjectCode(selectedPC);

          const selectedCurr = currencies.find(
            (c) => Number(c.value) === Number(data.currency)
          );
          setSelectedCurrency(selectedCurr || null);

          if (data.exchange_rate) {
            const selectedRate = exchangeRates.find(
              (rate) => Number(rate.id) === Number(data.exchange_rate)
            );

            setSelectedExchangeRate(
              selectedRate
                ? {
                    value: selectedRate.id,
                    label: String(selectedRate.value ?? ""),
                    dateLabel: selectedRate.date || "",
                    rateValue: Number(selectedRate.value),
                    rateDate: selectedRate.date,
                  }
                : null
            );
          } else {
            setSelectedExchangeRate(null);
          }

          // After setting cost_id, find associated supplier
          if (data.cost_id && costId.length > 0) {
            const cost_element = costId.find(d => d.value === data.cost_id);
            if (cost_element && cost_element.externalSupplier) {
              const matchingSupplier = allSuppliers.find(s => s.value === cost_element.externalSupplier);
              setFilteredSuppliers(matchingSupplier ? [matchingSupplier] : []);
            }
          }
        })
        .catch(error => console.error("Error loading invoice:", error));
    }
  }, [id, projectCodes, costId, allSuppliers, currencies, exchangeRates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Funcția pentru manipularea datelor
  const handleDatePick = (field, date) => {
    if (!date) {
      setFormData(prev => ({
        ...prev,
        [field]: "",
        [field + "_display"]: "",
      }));
      return;
    }

    const iso = format(date, "yyyy-MM-dd");
    const display = format(date, "dd.MM.yyyy");

    setFormData(prev => ({
      ...prev,
      [field]: iso,
      [field + "_display"]: display,
    }));

    // Pentru invoice_date, setează și month/year
    if (field === "invoice_date") {
      const year = date.getFullYear().toString();
      const monthValue = String(date.getMonth() + 1).padStart(2, '0');

      setFormData(prevData => ({
        ...prevData,
        year: year,
        month: monthValue
      }));
    }

    // Șterge eroarea dacă există
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_code) newErrors.project_code = "Required";
    if (!formData.cost_id) newErrors.cost_id = "Required";
    if (!formData.supplier) newErrors.supplier = "Required";
    if (!formData.invoice_number) newErrors.invoice_number = "Required";
    if (!formData.invoice_date) newErrors.invoice_date = "Required";
    if (!formData.bill_maturity) newErrors.bill_maturity = "Required";
    if (!formData.invoice_without_vat) newErrors.invoice_without_vat = "Required";
    if (!formData.supplier_vat) newErrors.supplier_vat = "Required";
    if (!formData.currency) newErrors.currency = "Required";

    if (!isSystemCurrency && !formData.exchange_rate) {
      newErrors.exchange_rate = "Exchange rate required";
    }

    // Adaugă validări pentru Total Invoice with VAT, Month și Year
    if (!formData.total_invoice_with_vat || formData.total_invoice_with_vat.trim() === "") {
      newErrors.total_invoice_with_vat = "Required";
    }
    if (!formData.month) newErrors.month = "Required";
    if (!formData.year) newErrors.year = "Required";

    if (!formData.medium_alert || formData.medium_alert === '') {
      newErrors.medium_alert = "Required";
    }
    if (!formData.critical_alert || formData.critical_alert === '') {
      newErrors.critical_alert = "Required";
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true); // 🔥 blocăm butonul și afișăm Saving…

    try {
      if (id) {
        await axiosInstance.put(`/external-resource-invoice/${id}/`, formData);
      } else {
        await axiosInstance.post("/external-resource-invoice/", formData);
      }

      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
        navigate(-1);
      }, 1500);

    } catch (error) {
      console.error("Error:", error.response?.data || error.message);

      if (error.response?.data && error.response.data.errors) {
        const backendErrors = error.response.data.errors;
        const newErrors = {};

        if (backendErrors.invoice_number) {
          newErrors.invoice_number = ["This number already exists."];
        }

        if (backendErrors.currency) {
          newErrors.currency = Array.isArray(backendErrors.currency)
            ? backendErrors.currency[0]
            : backendErrors.currency;
        }

        if (backendErrors.exchange_rate) {
          newErrors.exchange_rate = Array.isArray(backendErrors.exchange_rate)
            ? backendErrors.exchange_rate[0]
            : backendErrors.exchange_rate;
        }

        setErrors(newErrors);
        setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      } else {
        setErrors({ general: "A apărut o eroare neașteptată." });
      }

      setIsSaving(false); // ❗ reactualizăm doar dacă a fost eroare
    }
  };

  // Calculate total invoice with VAT
  useEffect(() => {
    if (formData.invoice_without_vat && formData.supplier_vat) {
      const invoiceWithoutVat = parseFloat(formData.invoice_without_vat);
      const vatPercentage = parseFloat(formData.supplier_vat);

      if (!isNaN(invoiceWithoutVat) && !isNaN(vatPercentage)) {
        const totalWithVat = invoiceWithoutVat * (1 + vatPercentage / 100);

        const systemAmounts = calculateSystemAmounts(
          invoiceWithoutVat,
          totalWithVat,
          formData.currency,
          selectedExchangeRate
        );

        setFormData((prevData) => ({
          ...prevData,
          total_invoice_with_vat: totalWithVat.toFixed(2),
          ...systemAmounts,
        }));

        // Șterge eroarea dacă există
        setFieldErrors(prev => {
          if (prev.total_invoice_with_vat) {
            return { ...prev, total_invoice_with_vat: "" };
          }
          return prev;
        });
      }
    } else {
      // Dacă unul dintre câmpuri este gol, setează totalul la ""
      setFormData((prevData) => ({
        ...prevData,
        total_invoice_with_vat: "",
        invoice_without_vat_system: "",
        total_invoice_with_vat_system: "",
      }));
    }
  }, [
    formData.invoice_without_vat,
    formData.supplier_vat,
    formData.currency,
    selectedExchangeRate,
    calculateSystemAmounts
  ]);

  // Stiluri pentru erori
  const errorStyle = {
    color: "#d32f2f",
    fontSize: "11px",
    marginTop: "-4px",
    marginBottom: "0px",
    lineHeight: "1",
  };

  // Component pentru afișarea erorilor pentru câmpurile individuale (non-paralele)
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div style={errorStyle}>
        {error}
      </div>
    );
  };

  // Component pentru afișarea erorilor pentru rândurile cu câmpuri paralele
  const RowErrorMessage = ({ errors }) => {
    const hasErrors = Object.values(errors).some(error => error);
    if (!hasErrors) return null;

    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "2px",
        height: "16px"
      }}>
        {Object.entries(errors).map(([field, error]) => (
          <div key={field} style={{ width: "48%" }}>
            {error && <div style={errorStyle}>{error}</div>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {showToast &&
        createPortal(
          <div className="global-toast-on-top">
            ✔ External Resource Invoice updated successfully!
          </div>,
          document.body
        )
      }

      <div className="edit-ext-resource-invoice-page">
        <div className="edit-container">
          <div className="header">
            <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
            <h2 className="edit-title">Edit External Resource Invoice</h2>
          </div>
        </div>
        <hr className="title-separator" />
        <div className="edit-container">
          <form onSubmit={handleSubmit} className="edit-form">
            {/* Project Code */}
            <div className="form-group">
              <label htmlFor="project_code" className="label">
                Project Code <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="project_code"
                name="project_code"
                options={projectCodes}
                classNamePrefix="react-select"
                value={selectedProjectCode}
                onChange={handleProjectCodeChange}
                placeholder="Select project code"
                components={animatedComponents}
                isClearable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: fieldErrors.project_code ? "#d32f2f" : provided.borderColor,
                    "&:hover": {
                      borderColor: fieldErrors.project_code ? "#d32f2f" : provided.borderColor,
                    },
                  }),
                }}
              />
              <ErrorMessage error={fieldErrors.project_code} />
            </div>

            {/* Cost Id */}
            <div className="form-group">
              <label htmlFor="cost_id" className="label">
                 Cost ID <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="cost_id"
                name="cost_id"
                classNamePrefix="react-select"
                options={filteredCosts}
                value={selectedCost}
                onChange={handleCostChange}
                placeholder={filteredCosts.length ? "Select cost" : "First select a project"}
                components={animatedComponents}
                isClearable
                isDisabled={!formData.project_code}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: fieldErrors.cost_id ? "#d32f2f" : provided.borderColor,
                    "&:hover": {
                      borderColor: fieldErrors.cost_id ? "#d32f2f" : provided.borderColor,
                    },
                  }),
                }}
              />
              <ErrorMessage error={fieldErrors.cost_id} />
            </div>

            {/* Supplier */}
            <div className="form-group">
              <label htmlFor="supplier" className="label">
                Supplier name <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="supplier"
                name="supplier"
                classNamePrefix="react-select"
                options={filteredSuppliers}
                value={filteredSuppliers.find((s) => s.value === formData.supplier) || null}
                onChange={handleSupplierChange}
                placeholder={
                  filteredSuppliers.length
                    ? "Select supplier"
                    : formData.cost_id
                      ? "No supplier assigned to this cost element"
                      : "First select a cost_element"
                }
                components={animatedComponents}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: fieldErrors.supplier ? "#d32f2f" : provided.borderColor,
                    "&:hover": {
                      borderColor: fieldErrors.supplier ? "#d32f2f" : provided.borderColor,
                    },
                  }),
                }}
                isDisabled={!formData.cost_id || filteredSuppliers.length === 0}
              />
              <ErrorMessage error={fieldErrors.supplier} />
            </div>

            {/* Invoice Number and Date - ROW 1 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="invoice_number" className="label">
                  Invoice number <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  id="invoice_number"
                  name="invoice_number"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  className={`input ${errors.invoice_number || fieldErrors.invoice_number ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.invoice_number ? "#d32f2f" : "" }}
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label className="label">
                  Invoice Date <span style={{ color: 'red' }}>*</span>
                </label>

                <div className="datepicker-container">
                  {/* INPUT NORMAL – ARATĂ CA TOATE CELELALTE */}
                  <input
                    type="text"
                    value={formData.invoice_date_display}
                    placeholder="dd.mm.yyyy"
                    className={`input ${fieldErrors.invoice_date ? "input-error" : ""}`}
                    readOnly
                    onClick={() => document.querySelector("#invoice_date_picker")?.focus()}
                  />

                  {/* DATEPICKER – ASCUNS */}
                  <DatePicker
                    id="invoice_date_picker"
                    selected={formData.invoice_date ? new Date(formData.invoice_date) : null}
                    onChange={(date) => handleDatePick("invoice_date", date)}
                    dateFormat="dd.MM.yyyy"
                    className="datepicker-hidden"
                    autoComplete="off"
                  />

                  {/* ICONIȚA */}
                  <span
                    className="calendar-icon-inside"
                    onClick={() => document.querySelector("#invoice_date_picker")?.focus()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <RowErrorMessage errors={{
              invoice_number: fieldErrors.invoice_number,
              invoice_date: fieldErrors.invoice_date
            }} />

            {errors.invoice_number && (
              <div className="error-message-global">
                {errors.invoice_number[0]}
              </div>
            )}

            {/* Bill Maturity and Invoice Without VAT - ROW 2 */}
            <div className="form-group-row">
              <div className="form-group">
                <label className="label">
                  Bill Maturity <span style={{ color: 'red' }}>*</span>
                </label>

                <div className="datepicker-container">
                  {/* Input vizibil */}
                  <input
                    type="text"
                    value={formData.bill_maturity_display}
                    placeholder="dd.mm.yyyy"
                    className={`input ${fieldErrors.bill_maturity ? "input-error" : ""}`}
                    readOnly
                    onClick={() => document.querySelector("#bill_maturity_picker")?.focus()}
                  />

                  {/* DatePicker ascuns */}
                  <DatePicker
                    id="bill_maturity_picker"
                    selected={formData.bill_maturity ? new Date(formData.bill_maturity) : null}
                    onChange={(date) => handleDatePick("bill_maturity", date)}
                    dateFormat="dd.MM.yyyy"
                    className="datepicker-hidden"
                  />

                  {/* Iconița */}
                  <span
                    className="calendar-icon-inside"
                    onClick={() => document.querySelector("#bill_maturity_picker")?.focus()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="invoice_without_vat" className="label">
                  Invoice Without VAT {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span style={{ color: 'red' }}> *</span>
                </label>
                <NumericFormat
                  id="invoice_without_vat"
                  name="invoice_without_vat"
                  value={formData.invoice_without_vat}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputMode="decimal"
                  className={`input ${fieldErrors.invoice_without_vat ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.invoice_without_vat ? "#d32f2f" : "" }}
                  placeholder="0,00"
                  autoComplete="off"
                  required
                  onValueChange={({ floatValue }) => {
                    setFormData((prev) => ({
                      ...prev,
                      invoice_without_vat: floatValue ?? "",
                    }));
                    if (errors.invoice_without_vat) setErrors((prev) => ({ ...prev, invoice_without_vat: null }));
                    if (fieldErrors.invoice_without_vat) setFieldErrors((prev) => ({ ...prev, invoice_without_vat: "" }));
                  }}
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              bill_maturity: fieldErrors.bill_maturity,
              invoice_without_vat: fieldErrors.invoice_without_vat
            }} />

            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">
                  Currency <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  options={currencies}
                  classNamePrefix="react-select"
                  value={selectedCurrency}
                  onChange={handleCurrencyChange}
                  placeholder="Select currency"
                  components={animatedComponents}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    ...selectPortalStyles,
                    control: (provided) => ({
                      ...provided,
                      borderRadius: "8px",
                      borderColor: fieldErrors.currency ? "#d32f2f" : provided.borderColor,
                      "&:hover": {
                        borderColor: fieldErrors.currency ? "#d32f2f" : provided.borderColor,
                      },
                    }),
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">
                  Exchange Rate {!isSystemCurrency && <span style={{ color: 'red' }}>*</span>}
                </label>
                <Select
                  options={exchangeOptions}
                  classNamePrefix="react-select"
                  value={selectedExchangeRate}
                  onChange={handleExchangeRateChange}
                  placeholder={
                    !formData.currency
                      ? "Select currency first"
                      : isSystemCurrency
                      ? "Not needed"
                      : exchangeOptions.length
                      ? "Latest rate selected automatically"
                      : "No rates"
                  }
                  components={animatedComponents}
                  isClearable
                  isDisabled={!formData.currency || isSystemCurrency}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  formatOptionLabel={(option, { context }) =>
                    context === "value"
                      ? option.label
                      : `${option.label}${
                          option.dateLabel
                            ? ` (${formatDateLabel(option.dateLabel)})`
                            : ""
                        }`
                  }
                  styles={{
                    ...selectPortalStyles,
                    control: (provided) => ({
                      ...provided,
                      borderRadius: "8px",
                      borderColor: fieldErrors.exchange_rate ? "#d32f2f" : provided.borderColor,
                      "&:hover": {
                        borderColor: fieldErrors.exchange_rate ? "#d32f2f" : provided.borderColor,
                      },
                    }),
                  }}
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              currency: fieldErrors.currency,
              exchange_rate: fieldErrors.exchange_rate
            }} />

            {/* Supplier VAT and Total Invoice with VAT - ROW 3 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="supplier_vat" className="label">
                  Supplier VAT (%) <span style={{ color: 'red' }}>*</span>
                </label>
                <NumericFormat
                  id="supplier_vat"
                  name="supplier_vat"
                  value={formData.supplier_vat}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputMode="decimal"
                  className={`input ${fieldErrors.supplier_vat ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.supplier_vat ? "#d32f2f" : "" }}
                  placeholder="0,00"
                  required
                  onValueChange={({ floatValue }) => {
                    setFormData((prev) => ({
                      ...prev,
                      supplier_vat: floatValue ?? "",
                    }));
                    if (errors.supplier_vat) setErrors((prev) => ({ ...prev, supplier_vat: null }));
                    if (fieldErrors.supplier_vat) setFieldErrors((prev) => ({ ...prev, supplier_vat: "" }));
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="total_invoice_with_vat" className="label">
                  Total Invoice with VAT {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span style={{ color: 'red' }}> *</span>
                </label>
                <NumericFormat
                  id="total_invoice_with_vat"
                  name="total_invoice_with_vat"
                  value={formData.total_invoice_with_vat}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  inputMode="decimal"
                  className={`input ${fieldErrors.total_invoice_with_vat ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.total_invoice_with_vat ? "#d32f2f" : "" }}
                  placeholder="0,00"
                  readOnly
                  disabled
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              supplier_vat: fieldErrors.supplier_vat,
              total_invoice_with_vat: fieldErrors.total_invoice_with_vat
            }} />

            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="invoice_without_vat_system" className="label">
                  Total System without VAT
                  {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                </label>
                <NumericFormat
                  id="invoice_without_vat_system"
                  name="invoice_without_vat_system"
                  value={formData.invoice_without_vat_system}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  customInput="input"
                  disabled
                  className="input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="total_invoice_with_vat_system" className="label">
                  Total System with VAT
                  {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                </label>
                <NumericFormat
                  id="total_invoice_with_vat_system"
                  name="total_invoice_with_vat_system"
                  value={formData.total_invoice_with_vat_system}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  customInput="input"
                  disabled
                  className="input"
                />
              </div>
            </div>

            {/* Medium Alert and Critical Alert - ROW 4 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="medium_alert" className="label">
                  Medium Alert (default 5 days) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="medium_alert"
                  type="number"
                  name="medium_alert"
                  value={formData.medium_alert}
                  onChange={handleChange}
                  className={`input ${fieldErrors.medium_alert ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.medium_alert ? "#d32f2f" : "" }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="critical_alert" className="label">
                  Critical Alert (default 1 day) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="critical_alert"
                  type="number"
                  name="critical_alert"
                  value={formData.critical_alert}
                  onChange={handleChange}
                  className={`input ${fieldErrors.critical_alert ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.critical_alert ? "#d32f2f" : "" }}
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              medium_alert: fieldErrors.medium_alert,
              critical_alert: fieldErrors.critical_alert
            }} />

            {/* Month and Year - ROW 5 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="month" className="label">
                  Month <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="month"
                  type="text"
                  name="month"
                  value={monthChoices.find(m => m.value === formData.month)?.label || ''}
                  onChange={handleChange}
                  className={`input ${fieldErrors.month ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.month ? "#d32f2f" : "" }}
                  disabled
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="year" className="label">
                  Year <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="year"
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className={`input ${fieldErrors.year ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.year ? "#d32f2f" : "" }}
                  disabled
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              month: fieldErrors.month,
              year: fieldErrors.year
            }} />

            {/* Remarks */}
            <div className="form-group">
              <label htmlFor="remarks" className="label">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                className="textarea"
              />
            </div>

            {/* Status */}
            <div className="form-group">
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: "#2e2e2e",
                  fontSize: "14px",
                }}
              >
                Status: {formData.payments_status}
              </Typography>
            </div>

            {/* Buttons */}
            <div className="form-group-row">
              <div className="button-group-inline">
                <button type="button" className="button discard" onClick={() => navigate(-1)}>
                  Discard
                </button>
                <button
                  type="submit"
                  className={`button save ${isSaving ? "disabled" : ""}`}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditExternalResourceInvoice;